import { useQuery } from '@tanstack/react-query';

import { getCowbellFactorAggregations } from '../../../console/customers/_services/dashboard.service';
import {
  buildFactorTrends,
  formatTrendWindow,
  type FactorTrends,
} from './factorTrends';

interface UseCowbellFactorTrendsArgs {
  readonly accountId?: string;
  /**
   * Unix seconds for the dashboard's user-adjustable date window. Typed to
   * accept strings because it reaches us straight from the parsed query
   * string, which does not coerce.
   */
  readonly before?: number | string;
  readonly after?: number | string;
}

interface UseCowbellFactorTrendsResult {
  readonly trends: FactorTrends;
  readonly trendWindowLabel: string;
}

/**
 * Per-factor movement over the dashboard's date window, from the risk trend
 * series. A failure here is deliberately silent: the trend column is an
 * enhancement, and the panel still reads correctly without it, so a broken
 * trend fetch must not toast or blank the factors.
 */
export const useCowbellFactorTrends = ({
  accountId,
  before,
  after,
}: UseCowbellFactorTrendsArgs): UseCowbellFactorTrendsResult => {
  const query = useQuery(
    ['cowbell-factor-trends', accountId, before, after],
    () =>
      getCowbellFactorAggregations({ accountId, before, after }).then(
        ({ data }: any) => buildFactorTrends(data)
      ),
    {
      enabled: Boolean(accountId),
      refetchOnWindowFocus: false,
    }
  );

  return {
    trends: query.data ?? {},
    trendWindowLabel: formatTrendWindow({ before, after }),
  };
};

export default useCowbellFactorTrends;
